/**
 * useNoteConfig — loads note types and tags from managed lists.
 *
 * Falls back to hardcoded defaults if the managed lists don't exist yet.
 */

import { useMemo } from 'react';
import { useManagedListItemsByName } from '@shared/entities/managed-list';
import {
  NOTE_TYPES_LIST_NAME,
  NOTE_TAGS_LIST_NAME,
  DEFAULT_NOTE_TYPES,
  DEFAULT_NOTE_TAGS,
  DEFAULT_TAG_COLOR,
  DEFAULT_TYPE_COLOR,
} from './noteConstants';

export interface NoteTypeOption {
  value: string;
  color: string;
  abbr: string;
}

export interface NoteTagOption {
  value: string;
  label: string;
  color: string;
}

/** Generate a 2-letter abbreviation from a label */
function makeAbbr(label: string): string {
  const words = label.split(/[\s/]+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

export function useNoteConfig() {
  const { data: typeItems } = useManagedListItemsByName(NOTE_TYPES_LIST_NAME);
  const { data: tagItems } = useManagedListItemsByName(NOTE_TAGS_LIST_NAME);

  const noteTypes = useMemo<NoteTypeOption[]>(() => {
    if (typeItems && typeItems.length > 0) {
      return typeItems.map(item => ({
        value: item.value,
        color: item.color ?? DEFAULT_TYPE_COLOR,
        abbr: makeAbbr(item.value),
      }));
    }
    // Fallback to defaults
    return DEFAULT_NOTE_TYPES.map(label => ({
      value: label,
      color: DEFAULT_TYPE_COLOR,
      abbr: makeAbbr(label),
    }));
  }, [typeItems]);

  const noteTags = useMemo<NoteTagOption[]>(() => {
    if (tagItems && tagItems.length > 0) {
      return tagItems.map(item => ({
        value: item.value,
        label: item.value,
        color: item.color ?? DEFAULT_TAG_COLOR,
      }));
    }
    // Fallback to defaults
    return DEFAULT_NOTE_TAGS.map(tag => ({
      value: tag,
      label: tag,
      color: DEFAULT_TAG_COLOR,
    }));
  }, [tagItems]);

  return { noteTypes, noteTags };
}
